import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Chip, Container, Grid, InputAdornment, Stack, SwipeableDrawer, TextField, Typography } from '@mui/material';
import { DatePicker, LoadingButton } from '@mui/lab';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as Yup from 'yup';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { FormProvider, RHFSwitch, RHFTextField } from '../../../components/hook-form';
import { useTheme } from '@mui/material/styles';
import { getPos, updatePosStatus, viewPOSTab } from '../../../redux/slices/posRedux/posRedux';
import moment from 'moment';
import { fCurrency, fCurrencyWithoutSymbol } from '../../../utils/formatNumber';
import { tableCellStyle, tableHeaderStyle, topicStyling } from '../../../utils/cssStyles';
import { accessVerify, capitalize, safeJSONParse } from '../../../utils/common';
import { toast } from 'react-toastify';
import { addCustomer, addLoyaltyRecord, getSalesCustomerDetails, getSalesCustomers, updateCustomer } from '../../../redux/slices/customerRedux/customerRedux';
import PermissionRequired from '../../errorPages/permissionRequired';

const addCustomerDefaultValues = {
  name: '',
  phone: '',
  birthday: null,
  sendSMS: true,
};

const normalizeCustomerPhoneInput = (phone = '') => {
  const digits = phone.replace(/[^0-9]/g, '');

  if (digits.startsWith('94')) {
    return digits.substring(2, 11);
  }

  if (digits.length === 10 && digits.startsWith('0')) {
    return digits.substring(1, 10);
  }

  if (digits.length === 12 && digits.startsWith('094')) {
    return digits.substring(3, 12);
  }

  return digits.substring(0, 9);
};

const getCustomerPhoneWithCountryCode = (phone = '') => {
  const localPhone = normalizeCustomerPhoneInput(phone);
  return localPhone ? `94${localPhone}` : '';
};

const formatCustomerBirthday = (birthday) => (birthday && moment(birthday).isValid() ? moment(birthday).format('YYYY-MM-DD') : null);

const formatCustomerBirthdayDisplay = (birthday) => (birthday && moment(birthday).isValid() ? moment(birthday).format('DD MMMM') : '-');

const getCustomerFormValuesFromRow = (customer) => {
  const birthday = customer?.customer_birthday && moment(customer.customer_birthday).isValid() ? moment(customer.customer_birthday) : null;

  return {
    name: customer?.customer_name || '',
    phone: normalizeCustomerPhoneInput(customer?.customer_phone || ''),
    birthday,
    sendSMS: customer?.sendSms === 1,
  };
};

export default function CustomerList() {
  const theme = useTheme();
  const dataModel = {
    id: null,
    name: null,
    description: null,
  };
  const tableRowStyle = {
    borderBottom: '1px solid #ddd',
  };

  const textTitleStyle = {
    fontSize: '12px',
    color: 'grey',
    marginRight: '10px',
  };

  const textInformationStyle = {
    fontSize: '15px',
    fontWeight: 500,
    paddingLeft: '15px',
  };

  const posLogStyle = {
    fontSize: '11px',
    borderBottom: '1px solid #bbbbbb',
    padding: '3px',
  };

  const hrLine = {
    borderBottom: '1px solid #555555',
    margin: '0px auto 20px auto',
    width: '100%',
  };

  const getOrderStatusStyling = (status) => ({
    fontSize: '30px',
    margin: '20px',
    fontWeight: 800,
    textAlign: 'center',
    color: 'white',
    backgroundColor: status === selectedObject?.pos_status ? getStatusColorCode(status) : '#d3d3d3',
    padding: '3px 20px',
    borderRadius: '25px',
    cursor: 'pointer',
    transition: 'background-color 0.4s',
    '&:hover': {
      backgroundColor: '#a1a1a1 !important',
    },
  });

  const { themeStretch } = useSettings();
  const [selectedObject, setSelectedObject] = useState(dataModel);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isAddCustomerPending, setIsAddCustomerPending] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isEditCustomerPending, setIsEditCustomerPending] = useState(false);
  const [addCustomerRequestTime, setAddCustomerRequestTime] = useState(null);
  const [editCustomerRequestTime, setEditCustomerRequestTime] = useState(null);
  const [preserveSelectedCustomerId, setPreserveSelectedCustomerId] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const { selectedLocation } = useSelector((state) => state?.location);
  const { allData, addData, error, selectedCustomerDetails } = useSelector((state) => state.customers);
  const [loyaltyPointData, setLoyaltyPointData] = useState({ amount: null });

  const AddCustomerSchema = Yup.object().shape({
    name: Yup.string().trim().required('Customer name is required'),
    phone: Yup.string()
      .required('Contact number is required')
      .test('is-valid-phone', 'Contact number must have 9 digits after 94', (value) => /^\d{9}$/.test(normalizeCustomerPhoneInput(value))),
    birthday: Yup.mixed()
      .nullable()
      .test('is-valid-birthday', 'Birthday must be a valid date', (value) => !value || moment(value).isValid())
      .test('is-not-future-birthday', 'Birthday cannot be in the future', (value) => !value || moment(value).isSameOrBefore(moment(), 'day')),
    sendSMS: Yup.boolean(),
  });

  const defaultValues = useMemo(() => addCustomerDefaultValues, []);

  const methods = useForm({
    resolver: yupResolver(AddCustomerSchema),
    defaultValues,
  });

  const editMethods = useForm({
    resolver: yupResolver(AddCustomerSchema),
    defaultValues,
  });

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const {
    control: editControl,
    getValues: getEditValues,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    setValue: setEditValue,
    formState: { isSubmitting: isEditSubmitting },
  } = editMethods;

  useEffect(() => {
    // dispatch(getCustomers());
    dispatch(getSalesCustomers());
  }, []);

  useEffect(() => {
    if (allData?.length > 0) {
      const mappedCustomerList = allData?.map((value) => {
        return {
          ...value,
        };
      });

      setCustomerList(mappedCustomerList);

      if (preserveSelectedCustomerId) {
        const updatedSelectedCustomer = mappedCustomerList.find((customer) => customer?.customer_id === preserveSelectedCustomerId);
        if (updatedSelectedCustomer) {
          setSelectedObject(updatedSelectedCustomer);
          dispatch(getSalesCustomerDetails(updatedSelectedCustomer.customer_id));
        }
        setPreserveSelectedCustomerId(null);
      } else {
        setIsModelOpen(false);
      }
    } else {
      setCustomerList([]);
      setPreserveSelectedCustomerId(null);
      setIsModelOpen(false);
    }
  }, [allData]);

  useEffect(() => {
    if (!selectedCustomerDetails?.customerId || Number(selectedCustomerDetails.customerId) !== Number(selectedObject?.customer_id)) {
      return;
    }

    setSelectedObject((prev) => ({
      ...prev,
      loyalty_data: safeJSONParse(selectedCustomerDetails?.data?.loyalty_data),
      pos_data: safeJSONParse(selectedCustomerDetails?.data?.pos_data),
    }));
  }, [selectedCustomerDetails, selectedObject?.customer_id]);

  useEffect(() => {
    if (isEditingCustomer && selectedObject?.customer_id) {
      resetEdit(getCustomerFormValuesFromRow(selectedObject));
    }
  }, [isEditingCustomer, resetEdit, selectedObject]);

  useEffect(() => {
    if (!addData?.time) {
      return;
    }

    // dispatch(getCustomers());
    dispatch(getSalesCustomers());
  }, [addData]);

  useEffect(() => {
    if (!isAddCustomerPending || !addCustomerRequestTime || !addData?.data || Number(addData?.time) < addCustomerRequestTime) {
      return;
    }

    toast.success('Customer added successfully');
    setIsAddCustomerPending(false);
    setIsAddCustomerOpen(false);
    reset(addCustomerDefaultValues);
  }, [addData, addCustomerRequestTime, isAddCustomerPending, reset]);

  useEffect(() => {
    if (!isAddCustomerPending || !addCustomerRequestTime || !error?.message || Number(error?.time) < addCustomerRequestTime) {
      return;
    }

    const message = typeof error.message === 'string' ? error.message : error.message?.message || 'Unable to add customer';
    toast.error(message);
    setIsAddCustomerPending(false);
  }, [addCustomerRequestTime, error, isAddCustomerPending]);

  useEffect(() => {
    if (!isEditCustomerPending || !editCustomerRequestTime || !addData?.data || Number(addData?.time) < editCustomerRequestTime) {
      return;
    }

    toast.success(addData?.data?.message || 'Customer updated successfully');
    setIsEditCustomerPending(false);
    setIsEditingCustomer(false);
  }, [addData, editCustomerRequestTime, isEditCustomerPending]);

  useEffect(() => {
    if (!isEditCustomerPending || !editCustomerRequestTime || !error?.message || Number(error?.time) < editCustomerRequestTime) {
      return;
    }

    const message = typeof error.message === 'string' ? error.message : error.message?.message || 'Unable to update customer';
    toast.error(message);
    setIsEditCustomerPending(false);
  }, [editCustomerRequestTime, error, isEditCustomerPending]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
      setIsEditingCustomer(false);
    } else {
      setIsModelOpen(true);
      setIsEditingCustomer(false);
      setSelectedObject({
        ...modelData,
        loyalty_data: [],
        pos_data: [],
      });
      dispatch(getSalesCustomerDetails(modelData?.customer_id));
    }
  };

  const handleCloseAddCustomer = () => {
    setIsAddCustomerOpen(false);
    setIsAddCustomerPending(false);
    reset(addCustomerDefaultValues);
  };

  const handlePhoneBlur = () => {
    const normalizedPhone = normalizeCustomerPhoneInput(getValues('phone'));
    setValue('phone', normalizedPhone, { shouldDirty: true, shouldValidate: Boolean(normalizedPhone) });
  };

  const handleEditPhoneBlur = () => {
    const normalizedPhone = normalizeCustomerPhoneInput(getEditValues('phone'));
    setEditValue('phone', normalizedPhone, { shouldDirty: true, shouldValidate: Boolean(normalizedPhone) });
  };

  const handleStartEditCustomer = () => {
    resetEdit(getCustomerFormValuesFromRow(selectedObject));
    setIsEditingCustomer(true);
  };

  const handleCancelEditCustomer = () => {
    resetEdit(getCustomerFormValuesFromRow(selectedObject));
    setIsEditCustomerPending(false);
    setIsEditingCustomer(false);
  };

  const onSubmitAddCustomer = (value) => {
    const localPhone = normalizeCustomerPhoneInput(value?.phone);
    const normalizedPhone = getCustomerPhoneWithCountryCode(localPhone);
    const birthday = formatCustomerBirthday(value?.birthday);

    setValue('phone', localPhone, { shouldDirty: true, shouldValidate: true });
    setAddCustomerRequestTime(moment.now());
    setIsAddCustomerPending(true);
    dispatch(
      addCustomer({
        name: value?.name?.trim(),
        phone: normalizedPhone,
        birthday,
        sendSMS: value?.sendSMS,
      })
    );
  };

  const onSubmitEditCustomer = (value) => {
    const localPhone = normalizeCustomerPhoneInput(value?.phone);
    const normalizedPhone = getCustomerPhoneWithCountryCode(localPhone);
    const birthday = formatCustomerBirthday(value?.birthday);

    setEditValue('phone', localPhone, { shouldDirty: true, shouldValidate: true });
    setEditCustomerRequestTime(moment.now());
    setIsEditCustomerPending(true);
    setPreserveSelectedCustomerId(selectedObject?.customer_id);
    dispatch(
      updateCustomer({
        id: selectedObject?.customer_id,
        name: value?.name?.trim(),
        phone: normalizedPhone,
        birthday,
        sendSMS: value?.sendSMS,
      })
    );
  };

  const getStatusColorCode = (type) => {
    if (type === 'Preparing') {
      return '#CE9600';
    } else if (type === 'Prepared') {
      return '#FF0000';
    } else if (type === 'Served') {
      return '#00921D';
    } else if (type === 'Refunded') {
      return '#FF0000';
    } else {
      return '#585858';
    }
  };

  const columns = [
    {
      accessorKey: 'customer_id',
      header: 'ID',
    },
    {
      accessorKey: 'customer_name',
      header: 'Customer Name',
    },
    {
      accessorKey: 'customer_phone',
      header: 'Phone',
    },
    {
      accessorKey: 'sendSms',
      header: 'Allowed For SMS',
      Cell: ({ cell, row }) => {
        return <>{row?.original?.sendSms === 1 ? 'Allowed' : 'Not Allowed'}</>;
      },
    },
    {
      accessorKey: 'customer_datetime',
      header: 'Join Date',
      Cell: ({ cell, row }) => {
        return <>{moment(row?.original?.customer_datetime).format('DD MMMM YYYY')}</>;
      },
    },
    {
      accessorKey: 'customer_birthday',
      header: 'Birthday',
      Cell: ({ cell, row }) => {
        return <>{formatCustomerBirthdayDisplay(row?.original?.customer_birthday)}</>;
      },
    },
    {
      accessorKey: 'total_amount',
      header: 'Loyalty Amount',
      Cell: ({ cell, row }) => {
        return <>{fCurrency(row?.original?.total_amount)}</>;
      },
    },
  ];

  const [rowSelection, setRowSelection] = useState(false);
  return (
    <Page title={`Customer List`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Customer List`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Sale',
              href: '',
            },
            { name: `Customer List` },
          ]}
          action={
            accessVerify('CUSTOMERS_ADD') ? (
              <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => setIsAddCustomerOpen(true)}>
                New Customer
              </Button>
            ) : (
              <></>
            )
          }
        />

        {accessVerify('CUSTOMERS_VIEW') ? (
          <DataGridTable
            name={'Pos Sales'}
            data={customerList}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowActions={false}
            enablePinning={false}
            enableRowSelection={false}
            isRowClickable={true}
            onRowClick={(row) => {
              manageModel(row?.original);
            }}
            renderRowActionItems={(value, closeMenu) => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor={'top'}
          open={isAddCustomerOpen}
          onOpen={() => setIsAddCustomerOpen(true)}
          onClose={handleCloseAddCustomer}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>Add Customer</h1>
            <div style={hrLine} />
          </Box>

          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitAddCustomer)}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={8}>
                <Stack spacing={3} sx={{ p: 3 }}>
                  <Card sx={{ p: 3 }}>
                    <Typography variant='subtitle1' padding='10px 0'>
                      Customer Info
                    </Typography>
                    <Stack spacing={3}>
                      <RHFTextField name='name' label='Customer Name' />
                      <Controller
                        name='phone'
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label='Contact Number'
                            placeholder='7XXXXXXXX'
                            value={field.value || ''}
                            error={!!error}
                            helperText={error?.message}
                            onChange={(event) => field.onChange(normalizeCustomerPhoneInput(event.target.value))}
                            onBlur={() => {
                              field.onBlur();
                              handlePhoneBlur();
                            }}
                            InputProps={{
                              startAdornment: <InputAdornment position='start'>94</InputAdornment>,
                            }}
                            inputProps={{
                              maxLength: 9,
                              inputMode: 'numeric',
                            }}
                          />
                        )}
                      />
                      <Controller
                        name='birthday'
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <DatePicker
                            label='Birthday'
                            value={field.value}
                            disableFuture
                            inputFormat='dd MMMM yyyy'
                            onChange={(newValue) => field.onChange(newValue)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                error={!!error}
                                helperText={error?.message}
                                inputProps={{
                                  ...params.inputProps,
                                  readOnly: true,
                                }}
                              />
                            )}
                          />
                        )}
                      />
                      <RHFSwitch name='sendSMS' label='Send SMS' />
                    </Stack>
                  </Card>
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack spacing={3} sx={{ p: 3 }}>
                  <Card sx={{ p: 3 }}>
                    <Stack spacing={3}>
                      <LoadingButton type='submit' variant='contained' size='large' loading={isSubmitting || isAddCustomerPending} style={{ width: '100%' }}>
                        Create Customer
                      </LoadingButton>
                      <Button color='info' variant='outlined' size='large' style={{ width: '100%' }} onClick={handleCloseAddCustomer}>
                        Close
                      </Button>
                    </Stack>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </FormProvider>
        </SwipeableDrawer>

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => {
            setIsModelOpen(false);
            setIsEditingCustomer(false);
          }}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ padding: '10px 0 0 10px' }}>
            <h1>Customer Details</h1>
            <div style={hrLine} />
          </Box>
          <Grid container>
            <Grid item xs={12} md={8}>
              <Stack spacing={3} sx={{ p: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={12}>
                    <Card sx={{ height: '33vh', overflowY: 'auto', p: 3 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <p style={topicStyling}>POS History</p>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={tableHeaderStyle}>Id</th>
                              <th style={tableHeaderStyle}>Date Time</th>
                              <th style={tableHeaderStyle}>status</th>
                              <th style={tableHeaderStyle}>Cash Drawer ID</th>
                              <th style={tableHeaderStyle}>Total Amount</th>
                              <th style={tableHeaderStyle}>Total Paid Amount</th>
                              <th style={tableHeaderStyle}>Balance Amount</th>
                              <th style={tableHeaderStyle}>Added By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedObject?.pos_data?.map((posData, index) => (
                              <tr key={posData?.pos_id} style={tableRowStyle}>
                                <td style={tableCellStyle}>{posData?.id}</td>
                                <td style={tableCellStyle}>{moment(posData?.datetime).format('DD/MM/YYYY HH:mm')}</td>
                                <td style={tableCellStyle}>{posData?.status}</td>
                                <td style={tableCellStyle}>{posData?.cash_drawers_id}</td>
                                <td style={tableCellStyle}>{fCurrency(posData?.total_amount)}</td>
                                <td style={tableCellStyle}>{fCurrency(posData?.total_paid_amount)}</td>
                                <td style={tableCellStyle}>{fCurrency(posData?.balance)}</td>
                                <td style={tableCellStyle}>{posData?.userName}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Card sx={{ height: '33vh', overflowY: 'auto', p: 3 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <p style={topicStyling}>Loyalty History</p>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={tableHeaderStyle}>Id</th>
                              <th style={tableHeaderStyle}>Date</th>
                              <th style={tableHeaderStyle}>Title</th>
                              <th style={tableHeaderStyle}>Amount</th>
                              <th style={tableHeaderStyle}>Added By</th>
                              <th style={tableHeaderStyle}>Validity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedObject?.loyalty_data?.map((loyaltyData, index) => (
                              <tr key={loyaltyData?.pos_id} style={tableRowStyle}>
                                <td style={tableCellStyle}>{loyaltyData?.id}</td>
                                <td style={tableCellStyle}>{moment(loyaltyData?.datetime).format('DD/MM/YYYY HH:mm')}</td>
                                <td style={tableCellStyle}>{loyaltyData?.title}</td>
                                <td style={tableCellStyle}> {fCurrency(loyaltyData?.amount)} </td>
                                <td style={tableCellStyle}> {loyaltyData?.userName} </td>
                                <td style={tableCellStyle}> {loyaltyData?.isValid === 1 ? 'Valid' : 'Removed'} </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={3} sx={{ p: 2 }}>
                <Card style={{ height: '25vh', padding: '20px' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <p style={topicStyling}>Manage Loyalty Points</p>
                    <Stack spacing={3} sx={{ p: 2 }}>
                      <TextField
                        type='number'
                        label={`Amount`}
                        value={loyaltyPointData?.amount || ''}
                        onChange={(event) => {
                          setLoyaltyPointData({
                            ...loyaltyPointData,
                            amount: event.target.value,
                          });
                        }}
                        onClick={(event) => {
                          event.target.select();
                        }}
                      />
                      <Button
                        variant='contained'
                        startIcon={<Iconify icon='eva:plus-fill' />}
                        onClick={() => {
                          if (loyaltyPointData?.amount) {
                            setPreserveSelectedCustomerId(selectedObject?.customer_id);
                            dispatch(
                              addLoyaltyRecord({
                                customerId: selectedObject?.customer_id,
                                amount: loyaltyPointData?.amount,
                                title: 'Manual Add',
                              })
                            );
                            setLoyaltyPointData({
                              ...loyaltyPointData,
                              amount: null,
                            });
                          } else {
                            toast.error('Please enter amount');
                          }
                        }}
                      >
                        Add Amount & Close
                      </Button>
                    </Stack>
                  </Box>
                </Card>
                <Card sx={{ minHeight: '23vh', alignItems: 'center', p: 3 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
                      <p style={topicStyling}>Customer Details</p>
                      {!isEditingCustomer && accessVerify('CUSTOMERS_UPDATE') && (
                        <Button size='small' variant='outlined' startIcon={<Iconify icon='eva:edit-fill' />} onClick={handleStartEditCustomer}>
                          Edit
                        </Button>
                      )}
                    </Stack>

                    {isEditingCustomer ? (
                      <FormProvider methods={editMethods} onSubmit={handleEditSubmit(onSubmitEditCustomer)}>
                        <Stack spacing={2}>
                          <RHFTextField name='name' label='Customer Name' />
                          <Controller
                            name='phone'
                            control={editControl}
                            render={({ field, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Contact Number'
                                placeholder='7XXXXXXXX'
                                value={field.value || ''}
                                error={!!error}
                                helperText={error?.message}
                                onChange={(event) => field.onChange(normalizeCustomerPhoneInput(event.target.value))}
                                onBlur={() => {
                                  field.onBlur();
                                  handleEditPhoneBlur();
                                }}
                                InputProps={{
                                  startAdornment: <InputAdornment position='start'>94</InputAdornment>,
                                }}
                                inputProps={{
                                  maxLength: 9,
                                  inputMode: 'numeric',
                                }}
                              />
                            )}
                          />
                          <Controller
                            name='birthday'
                            control={editControl}
                            render={({ field, fieldState: { error } }) => (
                              <DatePicker
                                label='Birthday'
                                value={field.value}
                                disableFuture
                                inputFormat='dd MMMM yyyy'
                                onChange={(newValue) => field.onChange(newValue)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                    inputProps={{
                                      ...params.inputProps,
                                      readOnly: true,
                                    }}
                                  />
                                )}
                              />
                            )}
                          />
                          <RHFSwitch name='sendSMS' label='Send SMS' />
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <LoadingButton type='submit' variant='contained' loading={isEditSubmitting || isEditCustomerPending} fullWidth>
                              Save Changes
                            </LoadingButton>
                            <Button color='info' variant='outlined' fullWidth onClick={handleCancelEditCustomer}>
                              Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      </FormProvider>
                    ) : (
                      <div sx={{ flexDirection: 'column' }}>
                        <div sx={{ alignItems: 'center', mb: 2 }}>
                          <table>
                            <tbody>
                              <tr>
                                <td style={textTitleStyle}>Loyalty Amount</td>
                                <td style={textInformationStyle}>{fCurrency(selectedObject?.total_amount)}</td>
                              </tr>
                              <tr>
                                <td style={textTitleStyle}>Joining Date Time</td>
                                <td style={textInformationStyle}>{moment(selectedObject?.customer_datetime).format('DD MMMM YYYY')}</td>
                              </tr>
                              <tr>
                                <td style={textTitleStyle}>Birthday</td>
                                <td style={textInformationStyle}>{formatCustomerBirthdayDisplay(selectedObject?.customer_birthday)}</td>
                              </tr>
                              <tr>
                                <td style={textTitleStyle}>Name</td>
                                <td style={textInformationStyle}>{selectedObject?.customer_name}</td>
                              </tr>
                              <tr>
                                <td style={textTitleStyle}>Phone</td>
                                <td style={textInformationStyle}>{selectedObject?.customer_phone}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </Box>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
